import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";

import { bindActionCreators, Dispatch } from "redux";

import { FilterSection, FilterItem, FilterItemField, PickerInput, RequiredState, DataColumnDef, InlineOverlay, Dropdown, FilterTextInput, Checkbox } from "@plex/react-components";

import { arrayHelpers } from "infrastructure/helpers";
import { ILocalizable, ILocale, commonMessages } from "infrastructure/localization";
import { ISettingsState } from "ui/app";
import { useLocalizedValues, useLocalizedDefaultLocale, useLocalizer } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";
import { ITenant, IEnterprise, IIndustry } from "ui/shared/models/common";

import { translationMessagesActions } from "../TranslationMessages.actions";
import { ITranslationMessagesManagerState, ITranslationMessagesManagerFilterData, ITenantsPickerSearchCriterion } from "../TranslationMessages.state";

interface IFilterProps {
  locales: ILocale[];
  supportedLocales?: ILocale[];
  currentLocaleIdentifier: string;
  industries: IIndustry[];
  enterprises: IEnterprise[];
  tenants: ITenant[];
  translatedBy: ILocalizable[];
  parents: ILocalizable[];
  applyFilter: (data: ITranslationMessagesManagerFilterData) => void;
  search: () => void;
  loadIndustries: (criterion: IPickerSearchCriterion<IIndustry>) => void;
  loadEnterprises: (criterion: IPickerSearchCriterion<IEnterprise>) => void;
  loadTenants: (criterion: ITenantsPickerSearchCriterion) => void;
  loadLocales: (criterion: IPickerSearchCriterion<ILocale>) => void;
  loadSupportedLocales: () => void;
}

export const TranslationMessagesManagerFilter: React.FC<IFilterProps> = ({
  locales,
  supportedLocales,
  currentLocaleIdentifier,
  industries,
  enterprises,
  tenants,
  translatedBy,
  parents,
  applyFilter,
  search,
  loadIndustries,
  loadEnterprises,
  loadTenants,
  loadLocales,
  loadSupportedLocales
}) => {
  const localize = useLocalizer();
  const defaultLocale = useLocalizedDefaultLocale();

  const localizedValues = useLocalizedValues(() => {
    return {
      locale: displayMessages.filter("locale"),
      localeIdentifier: displayMessages.property("localeIdentifier"),
      localeName: displayMessages.property("localeName"),
      industry: displayMessages.filter("industry"),
      enterprise: displayMessages.filter("enterprise"),
      tenant: displayMessages.filter("tenant"),
      text: displayMessages.filter("text"),
      messageId: displayMessages.filter("messageId"),
      translatedBy: displayMessages.filter("translatedBy"),
      parent: displayMessages.filter("parent"),
      showTextPreview: displayMessages.filter("showTextPreview"),
      code: displayMessages.property("code"),
      description: displayMessages.property("description"),
      all: commonMessages.property("all")
    };
  });

  const [state, setState] = useState<ITranslationMessagesManagerFilterData & { localePickerInitialized: boolean }>({
    localePickerInitialized: false,
    isCheckedShowPreviewText: false
  });
  const [pinned, setPinned] = useState(false);

  const onFilterChange = <T, P extends keyof ITranslationMessagesManagerFilterData>(value: T, prop: P) => {
    setState({ ...state, [prop]: value });
  };

  const onFilterSelect = <T, P extends keyof ITranslationMessagesManagerFilterData>(items: T[], prop: P) => {
    setState({ ...state, [prop]: items && items.length ? items[0] : undefined });
  };

  const onCheckboxChange = e => {
    setState({
      ...state,
      [e.currentTarget.id]: e.currentTarget.checked
    });
  };

  const selectedLocales = useMemo(() => {
    return arrayHelpers.createFrom(state.selectedLocale);
  }, [state.selectedLocale]);

  const selectedIndustries = useMemo(() => {
    return arrayHelpers.createFrom(state.selectedIndustry);
  }, [state.selectedIndustry]);

  const selectedEnterprise = useMemo(() => {
    return arrayHelpers.createFrom(state.selectedEnterprise);
  }, [state.selectedEnterprise]);

  const selectedTenant = useMemo(() => {
    return arrayHelpers.createFrom(state.selectedTenant);
  }, [state.selectedTenant]);

  useEffect(() => {
    loadSupportedLocales();
  }, [loadSupportedLocales]);

  useEffect(() => {
    if (supportedLocales && !state.localePickerInitialized) {
      setState(prevState => ({
        ...prevState,
        selectedLocale: supportedLocales.filter(e => e.localeIdentifier === currentLocaleIdentifier)[0] || defaultLocale,
        localePickerInitialized: true
      }));
    }
  }, [supportedLocales, currentLocaleIdentifier, state.localePickerInitialized, defaultLocale]);

  return (
    <FilterSection
      pinned={pinned}
      onSearch={() => {
        applyFilter(state);
        search();
      }}
      onPin={setPinned}
    >
      <FilterItem id="languageItem" labelText={localizedValues.locale} required={RequiredState.required}>
        <FilterItemField shouldCollapse={() => !state.selectedLocale}>
          <InlineOverlay show={!state.localePickerInitialized} spinnerPosition={"left"} showSpinner>
            <PickerInput
              autoPick
              dialogTitle={localizedValues.locale}
              data={locales}
              keySelector={row => (row ? row.localeIdentifier : "")}
              displaySelector={row => (row ? row.name : "")}
              selected={selectedLocales}
              onSelectionChanged={rows => onFilterSelect(rows, "selectedLocale")}
              onSearch={(q, cols) => loadLocales({ query: q, searchedColumns: cols })}
              searchText=""
            >
              <DataColumnDef<ILocale> key="1" id="localeIdentifier" title={localizedValues.localeIdentifier} valueSelector={row => row.localeIdentifier} searchable sortable>
                {row => row.localeIdentifier}
              </DataColumnDef>
              <DataColumnDef<ILocale> key="2" id="localeName" title={localizedValues.localeName} valueSelector={row => row.name} searchable sortable>
                {row => row.name}
              </DataColumnDef>
            </PickerInput>
          </InlineOverlay>
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.text}>
        <FilterItemField shouldCollapse={() => !state.filterText}>
          <FilterTextInput id="textItem" value={state.filterText} onChange={e => onFilterChange(e.currentTarget.value, "filterText")} />
        </FilterItemField>
      </FilterItem>
      <FilterItem id="translatedByItem" labelText={localizedValues.translatedBy}>
        <FilterItemField shouldCollapse={() => !state.selectedTranslatedBy}>
          <Dropdown
            id="translatedBy"
            placeholder={localizedValues.all}
            items={translatedBy}
            includeEmpty
            keySelector={item => (item as ILocalizable).messageId.value}
            displaySelector={item => localize((item as ILocalizable).messageId)}
            selected={arrayHelpers.createFrom(state.selectedTranslatedBy)}
            onSelectionChanged={items => onFilterSelect(items, "selectedTranslatedBy")}
          />
        </FilterItemField>
      </FilterItem>
      <FilterItem id="industryItem" labelText={localizedValues.industry}>
        <FilterItemField shouldCollapse={() => !state.selectedIndustry}>
          <PickerInput
            autoPick
            dialogTitle={localizedValues.industry}
            data={industries}
            keySelector={row => row.isiCv4Code}
            displaySelector={row => row.description}
            selected={selectedIndustries}
            onSelectionChanged={rows => onFilterSelect(rows, "selectedIndustry")}
            onSearch={(q, cols) => loadIndustries({ query: q, searchedColumns: cols })}
            searchText=""
          >
            <DataColumnDef<IIndustry> key="1" id="industryIsiCv4Code" title={localizedValues.code} valueSelector={row => row.isiCv4Code} searchable sortable>
              {row => row.isiCv4Code}
            </DataColumnDef>
            <DataColumnDef<IIndustry> key="2" id="industryDescription" title={localizedValues.description} valueSelector={row => row.description} searchable sortable>
              {row => row.description}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>
      <FilterItem id="enterpriseItem" labelText={localizedValues.enterprise} required={RequiredState.required}>
        <FilterItemField shouldCollapse={() => !state.selectedEnterprise}>
          <PickerInput
            autoPick
            dialogTitle={localizedValues.enterprise}
            data={enterprises}
            keySelector={row => (row ? row.enterpriseId : "")}
            displaySelector={row => (row ? row.name : "")}
            selected={selectedEnterprise}
            onSelectionChanged={rows => onFilterSelect(rows, "selectedEnterprise")}
            onSearch={(q, cols) => loadEnterprises({ query: q, searchedColumns: cols })}
            searchText=""
          >
            <DataColumnDef<IEnterprise> key="1" id="enterpriseName" title={localizedValues.enterprise} valueSelector={row => row.name} searchable sortable>
              {row => row.name}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>
      <FilterItem id="tenantItem" labelText={localizedValues.tenant}>
        <FilterItemField shouldCollapse={() => !state.selectedTenant}>
          <PickerInput
            autoPick
            dialogTitle={localizedValues.tenant}
            data={tenants}
            keySelector={row => (row ? row.tenantId : "")}
            displaySelector={row => (row ? row.name : "")}
            selected={selectedTenant}
            onSelectionChanged={rows => onFilterSelect(rows, "selectedTenant")}
            onSearch={(q, cols) => loadTenants({ query: q, searchedColumns: cols, enterpriseId: state.selectedEnterprise ? state.selectedEnterprise.enterpriseId : "" })}
            searchText=""
          >
            <DataColumnDef<ITenant> key="1" id="tenantName" title={localizedValues.tenant} valueSelector={row => row.name} searchable sortable>
              {row => row.name}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.messageId}>
        <FilterItemField shouldCollapse={() => !state.filterMessageId}>
          <FilterTextInput id="messageId" value={state.filterMessageId} onChange={e => onFilterChange(e.currentTarget.value, "filterMessageId")} />
        </FilterItemField>
      </FilterItem>
      <FilterItem id="parentItem" labelText={localizedValues.parent}>
        <FilterItemField shouldCollapse={() => !state.selectedParent}>
          <Dropdown
            id="parents"
            placeholder={localizedValues.all}
            includeEmpty
            items={parents}
            keySelector={item => (item as ILocalizable).messageId.value}
            displaySelector={item => localize((item as ILocalizable).messageId)}
            selected={arrayHelpers.createFrom(state.selectedParent)}
            onSelectionChanged={items => onFilterSelect(items, "selectedParent")}
          />
        </FilterItemField>
      </FilterItem>

      <FilterItem labelText={localizedValues.showTextPreview}>
        <FilterItemField shouldCollapse={() => !state.isCheckedShowPreviewText}>
          <Checkbox id="isCheckedShowPreviewText" checked={state.isCheckedShowPreviewText} onChange={onCheckboxChange} />
        </FilterItemField>
      </FilterItem>
    </FilterSection>
  );
};

export default connect(
  (state: { translationMessagesManager: ITranslationMessagesManagerState; settings: ISettingsState }) => {
    const filter = state.translationMessagesManager.filter;
    return {
      showPreviewText: filter.isCheckedShowPreviewText,
      locales: filter.locales,
      supportedLocales: filter.supportedLocales,
      currentLocaleIdentifier: state.settings.localSettings.locale,
      industries: filter.industries,
      enterprises: filter.enterprises,
      tenants: filter.tenants,
      parents: filter.parents,
      translatedBy: filter.translatedBy
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        applyFilter: translationMessagesActions.applyFilter,
        search: translationMessagesActions.search,
        loadEnterprises: translationMessagesActions.loadEnterprises,
        loadIndustries: translationMessagesActions.loadIndustries,
        loadTenants: translationMessagesActions.loadTenants,
        loadLocales: translationMessagesActions.loadLocales,
        loadSupportedLocales: translationMessagesActions.loadSupportedLocales
      },
      dispatch
    );
  }
)(TranslationMessagesManagerFilter);
